/**
 * Finance API surface.
 *
 * IBA-160: this file used to be a ~2,100-line monolith that also delegated ~24 calls to a localStorage mock
 * store (`finance-store.ts`, now deleted along with the draft→submit→approve→post workflow it backed, which
 * contradicted the immutable-posting model of ADR-0009). It is now a thin aggregator over per-concern modules
 * in `./finance/`; every consumer still imports the single `financeApi` object from here.
 */

import {
  activateFinanceAccount,
  createFinanceAccount,
  deactivateFinanceAccount,
  fetchFinanceAccountTypes,
  listFinanceAccounts,
  updateFinanceAccount,
} from "@/features/finance/services/finance/finance-accounts-api"
import {
  attachFinanceFundActivities,
  setFinanceFundDedication,
  createFinanceFund,
  createFinanceSetupFund,
  listFinanceFunds,
  reallocateFunds,
  setFinanceFundStatus,
  updateFinanceFund,
} from "@/features/finance/services/finance/finance-funds-api"
import {
  cancelFinanceTransaction,
  updateFinanceTransactionMetadata,
  createTransactionRecord,
  createTransfer,
  fetchBudgetLineMatches,
  listFinanceTransactionRecordPage,
  listFinanceTransactionRecords,
} from "@/features/finance/services/finance/finance-transactions-api"
import {
  downloadTransactionReceiptPdf,
  getReceiptConfig,
  getTransactionReceipt,
  getTransactionReceiptPreview,
  issueTransactionReceipt,
  previewReceiptConfig,
  updateReceiptConfig,
} from "@/features/finance/services/finance/finance-receipts-api"
import {
  createFinanceActivity,
  getFinanceActivity,
  listFinanceActivities,
  updateFinanceActivity,
} from "@/features/finance/services/finance/finance-activities-api"
import {
  getDepartmentFinanceSummary,
  getFinanceCurrentIncomeBalance,
  getFinanceCurrentIncomeBalances,
  getFinanceOverview,
} from "@/features/finance/services/finance/finance-overview-api"
import {
  createFinanceParishExpense,
  createFinanceParishIncome,
  listFinanceParishTransactions,
} from "@/features/finance/services/finance/finance-parish-api"
import {
  createAccountingAccount,
  getAccountingChart,
  getAccountingJournal,
  getAccountingLedger,
  getAccountingStatements,
  getAccountingTrialBalance,
  getFinanceHealth,
} from "@/features/finance/services/finance/finance-accounting-api"

import {
  approveFinanceTransferRequest,
  cancelFinanceTransferRequest,
  reverseFinanceTransferRequest,
  createFinanceTransferRequest,
  deriveTransferAmount,
  listSavedTransferCalculations,
  createSavedTransferCalculation,
  deleteSavedTransferCalculation,
  getFinanceTransferRequest,
  listFinanceTransferDestinations,
  listFinanceTransferRequests,
  rejectFinanceTransferRequest,
  submitFinanceTransferRequest,
  updateFinanceTransferRequest,
} from "@/features/finance/services/finance/finance-transfer-requests-api"

export { fetchActivityProgramIncomeBreakdown } from "@/features/finance/services/finance/finance-transactions-api"

export const financeApi = {
  getFinanceTransferRequest,
  listFinanceTransferDestinations,
  listFinanceTransferRequests,
  createFinanceTransferRequest,
  deriveTransferAmount,
  listSavedTransferCalculations,
  createSavedTransferCalculation,
  deleteSavedTransferCalculation,
  submitFinanceTransferRequest,
  updateFinanceTransferRequest,
  approveFinanceTransferRequest,
  rejectFinanceTransferRequest,
  cancelFinanceTransferRequest,
  reverseFinanceTransferRequest,
  getFinanceHealth,
  getAccountingChart,
  createAccountingAccount,
  getAccountingJournal,
  getAccountingLedger,
  getAccountingTrialBalance,
  getAccountingStatements,
  fetchFinanceAccountTypes,
  listFinanceAccounts,
  listFinanceFunds,
  createFinanceFund,
  createFinanceSetupFund,
  updateFinanceFund,
  reallocateFunds,
  setFinanceFundStatus,
  attachFinanceFundActivities,
  setFinanceFundDedication,
  createFinanceAccount,
  updateFinanceAccount,
  deactivateFinanceAccount,
  activateFinanceAccount,
  listFinanceTransactionRecords,
  listFinanceTransactionRecordPage,
  createTransactionRecord,
  createTransfer,
  fetchBudgetLineMatches,
  cancelFinanceTransaction,
  updateFinanceTransactionMetadata,
  getTransactionReceiptPreview,
  issueTransactionReceipt,
  getTransactionReceipt,
  downloadTransactionReceiptPdf,
  getReceiptConfig,
  updateReceiptConfig,
  previewReceiptConfig,
  listFinanceActivities,
  getFinanceActivity,
  createFinanceActivity,
  updateFinanceActivity,
  getFinanceOverview,
  listFinanceParishTransactions,
  createFinanceParishIncome,
  createFinanceParishExpense,
  getFinanceCurrentIncomeBalance,
  getFinanceCurrentIncomeBalances,
  getDepartmentFinanceSummary,
}
